import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { fetchSettlementRequestDetails } from 'services/apiSrv';
import styled from 'styled-components';
import { formatDate } from 'utils/helpers';


const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.5rem 1rem;
`;

const Title = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  margin-bottom: 2rem;
  color: #111827;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  transition: box-shadow 0.2s ease;
  
  &:hover {
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(to right, #f9fafb, #ffffff);
`;

const CardIcon = styled.div`
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  border-radius: 0.5rem;
  overflow: hidden;
  background: #e5e7eb;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const CardTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    color: #7c3aed; /* purple-600 inspired hover */
  }
`;

const CardContent = styled.div`
  padding: 1.5rem;
`;

const DescriptionList = styled.dl`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Term = styled.dt`
  font-size: 0.875rem;
  font-weight: 500;
  color: #6b7280;
`;

const Definition = styled.dd`
  margin: 0.25rem 0 0 0;
  font-size: 0.95rem;
  color: #111827;
  font-weight: ${props => (props.$bold ? '600' : '400')};
`;

const NestedList = styled(DescriptionList)`
  margin-top: 0.5rem;
  font-size: 0.875rem;
`;

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 50vh;
`;

const Spinner = styled.div`
  border: 4px solid #e5e7eb;
  border-top: 4px solid #7c3aed;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.span`
  margin-left: 1.25rem;
  color: #4b5563;
  font-weight: 500;
`;

// ─── Main Component ────────────────────────────────────────────────────

function SettlementRequest() {
  const { id } = useParams();

  const {
    data,
    isLoading,
    isError,
    error,
    isFetching,
  } = useQuery({
    queryKey: ['settlement-request', id],
    queryFn: () => fetchSettlementRequestDetails(id).then(res => res.data),
    enabled: !!id,
    staleTime: 3 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
  });

  if (!id) {
    return (
      <Card>
        <CardContent>No settlement request ID provided in URL</CardContent>
      </Card>
    );
  }

  if (isLoading || isFetching) {
    return (
      <LoaderContainer>
        <Spinner />
        <LoadingText>Loading settlement request...</LoadingText>
      </LoaderContainer>
    );
  }

  if (isError) {
    return (
      <Card>
        <CardContent>
          <div style={{ color: '#991b1b', fontWeight: 500 }}>Failed to load request</div>
          <div style={{ color: '#b91c1c', marginTop: '0.5rem' }}>
            {error?.message || 'Unknown error'}
          </div>
          <div style={{ fontSize: '0.875rem', color: '#dc2626', marginTop: '0.25rem' }}>
            Please try again later or contact support.
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card>
        <CardContent style={{ color: '#6b7280' }}>
          No data found for this request.
        </CardContent>
      </Card>
    );
  }

  return (
    <PageContainer>
      <Title>Settlement Request Details</Title>

      <Grid>
        <Card>
          <CardHeader>
            <CardIcon>
              <img
                src="https://cdn-images.toolify.ai/website-logos/20260114/1768370001_28551700_29086.webp"
                alt="Settlement Icon"
              />
            </CardIcon>
            <CardTitle>Request Information</CardTitle>
          </CardHeader>

          <CardContent>
            <DescriptionList>
              <div>
                <Term>Status</Term>
                <Definition>{data.settlementStatus || '—'}</Definition>
              </div>

              <div>
                <Term>Amount</Term>
                <Definition $bold>
                  {data.currency} {data.amount || 0}
                </Definition>
              </div>

              <div>
                <Term>Created</Term>
                <Definition>
                  {data.createdAt ? formatDate(data.createdAt) : '—'}
                </Definition>
              </div>

              <div>
                <Term>Type</Term>
                <Definition>{data?.settlmentRequestType || '—'}</Definition>
              </div>

              <div>
                <Term>Channel Details</Term>
                <Definition>
                  {data.settlmentChannelDetails ? (
                    <NestedList>
                      {Object.entries(data.settlmentChannelDetails).map(([key, value]) => (
                        <div key={key}>
                          <dt style={{ color: '#6b7280', fontWeight: 500 }}>
                            {key.replace(/([A-Z])/g, ' $1').trim()}
                          </dt>
                          <dd style={{ margin: '0.125rem 0 0.75rem 1rem', color: '#111827' }}>
                            {String(value)}
                          </dd>
                        </div>
                      ))}
                    </NestedList>
                  ) : '—'}
                </Definition>
              </div>
            </DescriptionList>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardIcon>
              {/* You can use a different icon here, or keep same / remove */}
              <img
                src="https://cdn-images.toolify.ai/website-logos/20260114/1768370001_28551700_29086.webp"
                alt="Remarks Icon"
              />
            </CardIcon>
            <CardTitle>Remarks</CardTitle>
          </CardHeader>

          <CardContent>
            <p style={{ color: '#374151', lineHeight: '1.6' }}>
              {data.settlementRemarks || 'No remarks provided.'}
            </p>
            {/* Place future content here – buttons, lists, file uploads, etc. */}
          </CardContent>
        </Card>
      </Grid>
    </PageContainer>
  );
}

export default SettlementRequest;